import { Check, Play } from 'iconoir-react';
import type { PropsWithChildren } from 'react';
import { cn } from '@listening/utils/react-helpers';
import * as m from '@/paraglide/messages';
import { ChapterRowContent } from './chapter-row';
import {
  RESEARCH_BODY_1,
  RESEARCH_BODY_2,
  RESEARCH_CITATION,
} from './demo-script';

export function SqueezeFeatures() {
  return (
    <section className='mx-auto flex max-w-[1080px] flex-col gap-10 px-4 py-11 sm:px-7 md:gap-14'>
      <ReversedFeatureRow>
        <FeatureVisual>
          <ReaderFeatureCard />
        </FeatureVisual>
        <FeatureCopy
          body={m.funnel_sq_fr1_body()}
          eyebrow={m.funnel_sq_fr1_eyebrow()}
          heading={m.funnel_sq_fr1_heading()}
        />
      </ReversedFeatureRow>
      <FeatureRow>
        <FeatureVisual>
          <ChaptersFeatureCard />
        </FeatureVisual>
        <FeatureCopy
          body={m.funnel_sq_fr2_body()}
          eyebrow={m.funnel_sq_fr2_eyebrow()}
          heading={m.funnel_listen_any_section()}
        />
      </FeatureRow>
      <ReversedFeatureRow>
        <FeatureVisual>
          <SourcesFeatureCard />
        </FeatureVisual>
        <FeatureCopy
          body={m.funnel_sq_fr3_body()}
          eyebrow={m.funnel_sq_fr3_eyebrow()}
          heading={m.funnel_sq_fr3_heading()}
        />
      </ReversedFeatureRow>
    </section>
  );
}

function FeatureRow({ children }: PropsWithChildren) {
  return <FeatureRowLayout>{children}</FeatureRowLayout>;
}

function ReversedFeatureRow({ children }: PropsWithChildren) {
  return (
    <FeatureRowLayout className='md:flex-row-reverse'>
      {children}
    </FeatureRowLayout>
  );
}

function FeatureRowLayout({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-6 md:flex-row md:gap-13',
        className,
      )}
    >
      {children}
    </div>
  );
}

function FeatureVisual({ children }: PropsWithChildren) {
  return <div className='flex min-w-0 flex-1 justify-center'>{children}</div>;
}

function FeatureCopy({
  eyebrow,
  heading,
  body,
}: {
  eyebrow: string;
  heading: string;
  body: string;
}) {
  return (
    <div className='min-w-0 flex-1'>
      <div className='mb-2.5 text-[12.5px] font-bold tracking-wide text-azure-455 uppercase'>
        {eyebrow}
      </div>
      <h3 className='mb-3 text-[22px] font-extrabold tracking-tight text-balance sm:text-[26px]'>
        {heading}
      </h3>
      <p className='text-base leading-relaxed text-steel-880'>{body}</p>
    </div>
  );
}

function ReaderFeatureCard() {
  return (
    <div className='w-full max-w-[430px] rounded-xl border border-steel-120 bg-white p-4 shadow-md'>
      <div className='mb-3 flex items-center gap-2.5'>
        <span className='grid size-[30px] shrink-0 place-items-center rounded-full bg-azure-595 text-white'>
          <Play aria-hidden className='size-3.5' fill='currentColor' />
        </span>
        <div className='flex-1 leading-tight'>
          <b className='block text-sm'>{m.funnel_demo_paper_title()}</b>
          <span className='text-xs text-steel-700'>
            Scarlett · {m.funnel_sq_reader_playing()}
          </span>
        </div>
        <span className='inline-flex items-center gap-1 rounded-full bg-jade-250 px-2.5 py-1 text-[11px] font-bold text-jade-750'>
          <Check aria-hidden className='size-3' strokeWidth={3.5} />
          {m.funnel_sq_reader_skips()}
        </span>
      </div>
      <div className='rounded-[10px] border border-steel-120 bg-mist-330 px-[18px] py-4 text-base leading-relaxed'>
        {RESEARCH_BODY_1}{' '}
        <span className='text-steel-260 line-through decoration-scarlet-400 decoration-[1.5px]'>
          {RESEARCH_CITATION}
        </span>{' '}
        {RESEARCH_BODY_2}
      </div>
    </div>
  );
}

function ChaptersFeatureCard() {
  return (
    <div className='w-full max-w-[340px] rounded-xl border border-steel-120 bg-white p-3.5 shadow-md'>
      <div className='px-2 pb-2 text-[10.5px] font-bold tracking-wider text-steel-700 uppercase'>
        {m.funnel_demo_chapters()}
      </div>
      <div className='flex flex-col gap-px'>
        <StaticChapterRow
          meta={m.funnel_demo_sec_done()}
          name={m.read_along_abstract()}
          status='done'
        />
        <StaticChapterRow
          meta='1:12'
          name={m.funnel_demo_sec_introduction()}
          status='now'
        />
        <StaticChapterRow
          meta='4:30'
          name={m.funnel_demo_sec_methods()}
          status='up'
        />
        <StaticChapterRow
          meta='8:05'
          name={m.funnel_demo_sec_results()}
          status='up'
        />
        <StaticChapterRow
          meta='10:40'
          name={m.funnel_demo_sec_discussion()}
          status='up'
        />
      </div>
      <div className='mt-1.5 border-t border-steel-120 px-2 pt-2 text-[11px] leading-snug text-steel-700'>
        {m.funnel_listen_any_section()}
      </div>
    </div>
  );
}

function StaticChapterRow({
  status,
  name,
  meta,
}: {
  status: 'done' | 'now' | 'up';
  name: string;
  meta: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg px-2 py-1.5',
        status === 'now' && 'bg-mist-385',
      )}
    >
      <ChapterRowContent meta={meta} name={name} status={status} />
    </div>
  );
}

function SourcesFeatureCard() {
  return (
    <div className='w-full max-w-[500px] rounded-xl border border-steel-120 bg-white p-4 shadow-md'>
      <div className='mb-4 flex flex-wrap justify-center gap-1'>
        <SourceChip emoji='📄' label={m.homepage_features_upload_pdf()} />
        <SourceChip emoji='🔗' label={m.funnel_src_web()} />
        <SourceChip emoji='📷' label={m.funnel_src_photo()} />
        <SourceChip emoji='📧' label={m.common_email()} />
        <SourceChip emoji='📖' label={m.funnel_src_epub()} />
      </div>
      <div className='flex items-center gap-3 rounded-[10px] border border-steel-120 bg-mist-165 px-4 py-3'>
        <span className='text-xl'>✍️</span>
        <div>
          <b className='block text-[13.5px]'>{m.funnel_saved_to_notes()}</b>
          <span className='text-[12.5px] text-steel-700'>
            {m.funnel_sq_capture_snippet()}
          </span>
        </div>
      </div>
    </div>
  );
}

function SourceChip({ emoji, label }: { emoji: string; label: string }) {
  return (
    <span className='inline-flex items-center gap-2 rounded-full border border-steel-120 bg-mist-385 px-2 py-1.5 text-[11.5px] font-semibold whitespace-nowrap'>
      <span aria-hidden>{emoji}</span>
      {label}
    </span>
  );
}
